import React, { useEffect } from "react";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation, Trans } from "next-i18next";
import { GetStaticPropsContext } from "next";
import { loadCurrentpage } from "../../store/slices/app-slice";
import SummaryItem from "../../components/ContentPages/SummaryItem";
import Paragraph from "../../components/ContentPages/Paragraph";
import ParagraphText from "../../components/ContentPages/ParagraphText";
import Border from "../../components/ContentPages/Border";
import { Locale } from "../../src/types";
import { getIsOnMaintenance } from "../../helpers";

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  const currentLocale = locale as Locale;
  const isOnMaintenance = await getIsOnMaintenance();

  if (isOnMaintenance) {
    return {
      redirect: {
        permanent: false,
        destination: "/maintenance",
      },
    };
  }

  return {
    props: {
      ...(await serverSideTranslations(currentLocale, [
        "common",
        "terms-conditions",
      ])),
    },
  };
}

function TermsConditions() {
  const { t } = useTranslation("terms-conditions");
  const router = useRouter();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadCurrentpage("Terms Conditions"));
  }, []);

  return (
    <div className="w-full bg-black flex justify-center text-white">
      <div className="lg:w-[1441px] w-full md:pl-[108px] pl-4 md:pr-[108px] pr-4 md:mt-[213px] mt-[113px]">
        <div className="md:w-[632px] w-full display-4 md:display-2 uppercase">
          <Trans
            components={{
              colored: <span className="text-primary" />,
            }}
            i18nKey="terms-conditions:title"
          />
        </div>

        <Border additionalClasses="mt-20 md:w-[632px] w-full">
          <ParagraphText>
            <Trans
              components={{
                linked: (
                  <a className="border-b hover:border-0" href="#content12" />
                ),
              }}
              i18nKey="terms-conditions:note.part1"
            />
          </ParagraphText>
          <ParagraphText>
            <Trans i18nKey="terms-conditions:note.part2" />
          </ParagraphText>
        </Border>

        <div className="mt-20 flex flex-col gap-4 font-['Inter'] highlightText underline md:w-[632px] w-full">
          <SummaryItem pRef="content1">{t("point1.title")}</SummaryItem>
          <SummaryItem pRef="content2">{t("point2.title")}</SummaryItem>
          <SummaryItem pRef="content3">{t("point3.title")}</SummaryItem>
          <SummaryItem pRef="content4">{t("point4.title")}</SummaryItem>
          <SummaryItem pRef="content5">{t("point5.title")}</SummaryItem>
          <SummaryItem pRef="content6">{t("point6.title")}</SummaryItem>
          <SummaryItem pRef="content7">{t("point7.title")}</SummaryItem>
          <SummaryItem pRef="content8">{t("point8.title")}</SummaryItem>
          <SummaryItem pRef="content9">{t("point9.title")}</SummaryItem>
          <SummaryItem pRef="content10">{t("point10.title")}</SummaryItem>
          <SummaryItem pRef="content11">{t("point11.title")}</SummaryItem>
          <SummaryItem pRef="content12">{t("point12.title")}</SummaryItem>
        </div>

        <div className="mt-20 flex flex-col gap-14 font-['Inter'] md:w-[632px] w-full md:pb-[126px] pb-20">
          <Paragraph pRef="content1" title={t("point1.title")}>
            <ParagraphText>
              <Trans
                components={{
                  linked: (
                    <a className="border-b hover:border-0" href="#content12" />
                  ),
                }}
                i18nKey="terms-conditions:point1.content.part1"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point1.content.part2"
              />
            </ParagraphText>
            <Border>
              <ParagraphText>
                <Trans
                  components={{
                    linked: (
                      <button
                        type="button"
                        className="border-b hover:border-black cursor-pointer"
                        onClick={() => router.push("/faq")}
                      />
                    ),
                  }}
                  i18nKey="terms-conditions:point1.content.note.part1"
                />
              </ParagraphText>
              <ParagraphText>
                <Trans
                  components={{
                    linked: (
                      <a
                        className="border-b hover:border-0"
                        href="https://www.ledger.com/academy"
                        target="_blank"
                        rel="noreferrer"
                      />
                    ),
                  }}
                  i18nKey="terms-conditions:point1.content.note.part2"
                />
              </ParagraphText>
              <ParagraphText>
                <Trans i18nKey="terms-conditions:point1.content.note.part3" />
              </ParagraphText>
            </Border>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point1.content.part3" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content2" title={t("point2.title")}>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point2.content.part1" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point2.content.part2" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point2.content.part3" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content3" title={t("point3.title")}>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point3.content.part1" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point3.content.part2" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content4" title={t("point4.title")}>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point4.content.part1" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point4.content.part2" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point4.content.part3" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content5" title={t("point5.title")}>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point5.content.part1" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point5.content.part2" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content6" title={t("point6.title")}>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point6.content.part1" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point6.content.part2" />
            </ParagraphText>
            <Border>
              <ParagraphText>
                <Trans i18nKey="terms-conditions:point6.content.note.part1" />
              </ParagraphText>
              <ParagraphText>
                <Trans i18nKey="terms-conditions:point6.content.note.part2" />
              </ParagraphText>
              <ParagraphText>
                <Trans
                  components={{
                    linked: (
                      <a
                        className="border-b hover:border-0"
                        href="https://www.ledger.com/academy"
                        target="_blank"
                        rel="noreferrer"
                      />
                    ),
                  }}
                  i18nKey="terms-conditions:point6.content.note.part3"
                />
              </ParagraphText>
            </Border>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point6.content.part3" />
            </ParagraphText>
            <Border>
              <ParagraphText additionalClasses="self-center">
                <Trans i18nKey="terms-conditions:point6.content.disclaimer.title" />
              </ParagraphText>
              <ParagraphText>
                <Trans i18nKey="terms-conditions:point6.content.disclaimer.part1" />
              </ParagraphText>
              <ParagraphText>
                <Trans i18nKey="terms-conditions:point6.content.disclaimer.part2" />
              </ParagraphText>
              <ParagraphText>
                <Trans i18nKey="terms-conditions:point6.content.disclaimer.part3" />
              </ParagraphText>
            </Border>
          </Paragraph>

          <Paragraph pRef="content7" title={t("point7.title")}>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point7.content.part1" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point7.content.part2" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content8" title={t("point8.title")}>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point8.content.part1" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point8.content.part2" />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  linked: (
                    <a
                      className="border-b hover:border-0"
                      href="https://www.ledger.com/pages/privacy-policy"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                  secondLinked: (
                    <a
                      className="border-b hover:border-0"
                      href="https://shop.ledger.com/pages/cookie-policy"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
                i18nKey="terms-conditions:point8.content.part3"
              />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content9" title={t("point9.title")}>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point9.content.part1" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point9.content.part2" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point9.content.part3" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content10" title={t("point10.title")}>
            <ParagraphText additionalClasses="pl-8">
              <Trans i18nKey="terms-conditions:point10.content.point1.title" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point10.content.point1.part1" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point10.content.point1.part2" />
            </ParagraphText>
            <ParagraphText additionalClasses="pl-8">
              <Trans i18nKey="terms-conditions:point10.content.point2.title" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point10.content.point2.part1" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point10.content.point2.part2" />
            </ParagraphText>
            <ParagraphText additionalClasses="pl-8">
              <Trans i18nKey="terms-conditions:point10.content.point3.title" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point10.content.point3.part1" />
            </ParagraphText>
            <ParagraphText additionalClasses="pl-8">
              <Trans i18nKey="terms-conditions:point10.content.point4.title" />
            </ParagraphText>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point10.content.point4.part1" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content11" title={t("point11.title")}>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point11.content.part1" />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  linked: (
                    <a
                      className="border-b hover:border-0"
                      href="https://support.ledgerwallet.com/hc/en-us"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
                i18nKey="terms-conditions:point11.content.part2"
              />
            </ParagraphText>
            <Border>
              <ParagraphText>
                <Trans i18nKey="terms-conditions:point11.content.note" />
              </ParagraphText>
            </Border>
            <ParagraphText>
              <Trans i18nKey="terms-conditions:point11.content.part3" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content12" title={t("point12.title")}>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition1"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition2"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition3"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition4"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition5"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition6"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition7"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition8"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition9"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition10"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition11"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition12"
              />
            </ParagraphText>
            <ParagraphText>
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="terms-conditions:point12.content.definition13"
              />
            </ParagraphText>
          </Paragraph>
        </div>
      </div>
    </div>
  );
}

export default TermsConditions;
